/** Compile Ace failed-check fixes into a task-thread prompt. */

import type { AceArtefactRating, AceFixRecommendation, PhaseCriteria } from "./types";

/** Reviewer-only steps — strip from prompts sent to the task agent. */
const REVIEWER_ONLY_PREFIXES: RegExp[] = [
  /^open the pdf preview(?:\s+and)?\s*/i,
  /^open (?:the )?cheat sheet preview(?:\s+and)?\s*/i,
  /^open (?:the )?loa preview(?:\s+and)?\s*/i,
  /^open document preview(?:\s+and)?\s*/i,
  /^look up [^.]+ on trustnet(?:\s+and)?\s*/i,
  /^search [^.]+ on morningstar(?:\s+and)?\s*/i,
  /^check (?:the )?fund on trustnet(?:\s+and)?\s*/i,
  /^open plannr documents?(?:\s+folder)?(?:\s+and)?\s*/i,
];

const REVIEWER_ONLY_INLINE: RegExp[] = [
  /\s*;\s*open the pdf preview\b[^.]*\.?/gi,
  /\s+or open the pdf preview\b[^.]*\.?/gi,
  /\s+via the provider to rule out[^.]*\.?\s*/gi,
];

/** Remove human verification steps from Ace fix copy before sending to the agent. */
export function sanitizeFixForWorkerPrompt(text: string): string {
  let out = text.trim();
  if (!out) return "";

  let changed = true;
  while (changed) {
    changed = false;
    for (const pattern of REVIEWER_ONLY_PREFIXES) {
      const next = out.replace(pattern, "");
      if (next !== out) {
        out = next.trim();
        changed = true;
      }
    }
  }

  for (const pattern of REVIEWER_ONLY_INLINE) {
    out = out.replace(pattern, "").trim();
  }

  out = out.replace(/^[,.;:\s]+/, "").replace(/\s+/g, " ").trim();
  if (out.length > 0) {
    out = out.charAt(0).toUpperCase() + out.slice(1);
  }
  return out;
}

export function verificationLinkLabel(recommendation: AceFixRecommendation): string {
  const label = recommendation.action_label?.trim();
  if (label) return label;
  const url = (recommendation.action_url ?? "").toLowerCase();
  if (url.includes("trustnet")) return "Check fund on Trustnet";
  if (url.includes("morningstar")) return "Search fund on Morningstar";
  if (url.includes("plannr")) return "Open Plannr documents folder";
  if (url.includes("/pdf") || url.includes("preview")) return "Open document preview";
  return "Open verification link";
}

export function compileArtefactFixPrompt(
  artefactTitle: string,
  criteria: PhaseCriteria,
  aceArtefact: AceArtefactRating,
): string {
  const failed = aceArtefact.checks.filter((check) => check.answer === "fail");
  if (failed.length === 0) return "";

  const lines = [
    `Please fix the issues found in ${artefactTitle}:`,
    "",
  ];

  for (const check of failed) {
    const label = criteria.checks.find((item) => item.id === check.id)?.label ?? check.id;
    const rawFix = check.recommendation?.text?.trim() || check.reason?.trim();
    const fix = rawFix ? sanitizeFixForWorkerPrompt(rawFix) : "";
    if (fix) {
      lines.push(`- ${label}: ${fix}`);
    }
  }

  lines.push("", "Re-run the task when the fixes are in place.");
  return lines.join("\n");
}
